// src/routes/MediaSelection/MediaSelectionResult.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Popover,
  Tabs,
  Tab
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { bg } from '../../utils/darkModeAdapter';

import AdviceContentBox from '../../components/AdviceContentBox';
import MediaSelectionCard from './MediaSelectionCard';
import MediaInquiryPage from './MediaInquiryPage';
import ScientificAnalysisSection from './ScientificAnalysisSection';

export default function MediaSelectionResult({ isLoading, planA, planB, advice, scientificAnalysis }) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0); // 0: プランA, 1: プランB
  const [showInquiryPage, setShowInquiryPage] = useState(false);

  // お得情報のPopover管理（hoverで表示）
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const handleMouseEnter = (event) => {
    setPopoverAnchor(event.currentTarget);
  };
  const handleMouseLeave = () => {
    setPopoverAnchor(null);
  };
  const openPopover = Boolean(popoverAnchor);

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };
  
  // MediaInquiryPageから戻ってきた時にポップオーバーを閉じる
  useEffect(() => {
    if (!showInquiryPage) {
      setPopoverAnchor(null);
    }
  }, [showInquiryPage]);

  const outerPaperStyle = {
    p: 3,
    width: '100%',
    maxWidth: 1200,
    margin: '40px auto',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    borderRadius: 2,
    backgroundColor: bg(theme).card,
    position: 'relative',
  };

  // お問い合わせページを表示
  const handleShowInquiry = () => {
    setShowInquiryPage(true);
  };

  // お問い合わせページを閉じる
  const handleCloseInquiry = () => {
    setShowInquiryPage(false);
  };

  // 現在表示しているプランを取得
  const getCurrentPlan = () => activeTab === 1 ? planB : planA;

  // ロード中 or プラン未取得チェック
  const hasPlanA = planA && planA.planName;
  const hasPlanB = planB && planB.planName;
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', mt: 2 }}>
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          ロード中...
        </Typography>
      </Paper>
    );
  }
  if (!hasPlanA && !hasPlanB) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', mt: 2 }}>
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          プラン情報がありません（または取得中です）。
        </Typography>
      </Paper>
    );
  }

  // お問い合わせページが表示されている場合
  if (showInquiryPage) {
    return (
      <MediaInquiryPage 
        plan={getCurrentPlan()} 
        onClose={handleCloseInquiry} 
      />
    );
  }

  return (
    <Paper sx={outerPaperStyle}>
      {advice && (
        <Box sx={{ mb: 3 }}>
          <AdviceContentBox adviceObj={advice} />
        </Box>
      )}

      {/* 科学的分析セクション */}
      {scientificAnalysis && (
        <ScientificAnalysisSection scientificAnalysis={scientificAnalysis} />
      )}

      {/* タブ切り替え */}
      {hasPlanA && hasPlanB && (
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            mb: 3,
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              minHeight: 48,
            },
            '& .Mui-selected': {
              fontWeight: 700,
            },
          }}
        >
          <Tab label="Aプラン" />
          <Tab label="Bプラン" />
        </Tabs>
      )}

      {/* プランコンテンツ */}
      <Box
        component={motion.div}
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      >
        {activeTab === 0 && hasPlanA && (
          <MediaSelectionCard
            plan={planA}
            planLabel="Aプラン"
            onShowInquiry={handleShowInquiry}
            handleMouseEnter={handleMouseEnter}
            handleMouseLeave={handleMouseLeave}
          />
        )}
        {activeTab === 1 && hasPlanB && (
          <MediaSelectionCard
            plan={planB}
            planLabel="Bプラン"
            onShowInquiry={handleShowInquiry}
            handleMouseEnter={handleMouseEnter}
            handleMouseLeave={handleMouseLeave}
          />
        )}
      </Box>

      {/* Popover（マウスオーバーで出す） */}
      <Popover
        open={openPopover}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{
          pointerEvents: 'none', // ホバーを自然にする
          '.MuiPaper-root': {
            p: 0,
            borderRadius: '12px',
            boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
            maxWidth: 280,
            overflow: 'visible',
            border: activeTab === 1
              ? '1px solid rgba(255,152,0,0.2)'
              : '1px solid rgba(38,198,218,0.2)',
            position: 'relative',
            // 吹き出し用の疑似要素
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderTop: activeTab === 1
                ? '10px solid rgba(255,152,0,0.2)'
                : '10px solid rgba(38,198,218,0.2)',
              zIndex: 0,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              bottom: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderTop: `10px solid ${bg(theme).card}`,
              zIndex: 1,
            },
          }
        }}
      >
        <Box sx={{ 
          p: 2, 
          bgcolor: bg(theme).card,
          borderRadius: '12px',
        }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            お得情報
          </Typography>
          <Typography variant="body2">
            お申し込み後すぐに担当がサポート。複数媒体同時申し込みで料金がお得に！
          </Typography>
        </Box>
      </Popover>
    </Paper>
  );
}